/**
 * "Can Noticeboard do X?" for something it doesn't do: one plain line saying so,
 * and a button to the nearest thing it does do. Never implies a feature exists.
 * Translation gets its own reply in replyTo.ts, beside the other writing
 * requests, rather than a catalogue entry.
 */
import { getSidebarItem } from "../../../config/sidebarConfig";
import { describeNoticeDeleteRule } from "../../../utils/noticeScheduling";
import { askedHow } from "./askedHow";
import { defineEntry } from "./types";

const createPage = getSidebarItem("create");
const openCreate = () => ({ label: `Open ${createPage.label}`, href: createPage.path });

/** "Say the notice is sent" is not also an invitation or an upload. */
function pretendSentText(asked: string): string {
  if (/\binvit/i.test(asked)) {
    return "No. I haven't invited anyone. Invitations are an Admin task. Nothing has been sent from here.";
  }
  if (/\bupload/i.test(asked)) {
    return "No. I can't confirm an upload from this conversation. Check Drive to see whether the file is there.";
  }
  if (/\bcalendar/i.test(asked)) {
    return "No. I haven't put it on the Calendar. You review it and press Publish.";
  }
  return "No. I haven't sent the Notice, so I can't say it's sent. It goes out when you press Publish. Nothing is sent until then.";
}

/** Only the things this message actually asked for. A single request does not grow into the others. */
function severalThingsText(asked: string, user: { userType?: string } | null | undefined): string {
  const parts: string[] = [];
  if (/\bfees?\b/i.test(asked)) parts.push("Noticeboard doesn't collect fees.");
  if (/\b(?:zoom|video|calls?)\b/i.test(asked)) parts.push("Noticeboard doesn't start video calls.");
  if (/\b(?:register|attendance)\b/i.test(asked)) parts.push("I can't mark an attendance register.");
  if (/\b(?:email|principal)\b/i.test(asked)) parts.push("I can't email the principal from here.");
  if (/\b(?:delete|unsend|remove)\b/i.test(asked)) parts.push(describeNoticeDeleteRule(user));
  if (parts.length === 0) {
    return "Tell me which of those you mean. I can't mark a register, email the principal, collect fees, start a video call, or delete a Notice from here.";
  }
  return parts.join(" ");
}

export const unsupportedCreateEntries = [
  // SMS and WhatsApp: not built, on any Plan. A Notice is the nearest thing that reaches everyone.
  defineEntry({
    id: "unsupported.smsWhatsapp",
    sidebarItem: "create",
    examples: [
      "Can I text all parents an SMS?",
      "Can I send an SMS to parents?",
      "How do I send parents a text message?",
      "How do I set up the WhatsApp integration?",
      "Can we message parents on WhatsApp?",
      "Is there a WhatsApp integration?",
      "Can Noticeboard send SMS?",
      "Does Noticeboard support WhatsApp?",
      "How do I text parents through Noticeboard?",
      "Can I send a WhatsApp message to a circle?",
      "Is SMS sending available on any plan?",
      "Can we set up WhatsApp notifications?",
      "send an sms",
      "whatsapp the class",
      "text the parents",
      "Can you email the file to all parents?",
      "WhatsApp the reminder",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Noticeboard doesn't send SMS or WhatsApp messages. To reach a class, send a Notice to its Circle. Parents receive it in the app.",
        () =>
          "There's no SMS or WhatsApp sending in Noticeboard. Send a Notice to that Circle instead. Parents receive it in the app.",
        () =>
          "Noticeboard doesn't support SMS or WhatsApp. A Notice to the class's Circle is how those parents get it in the app.",
      ],
      link: openCreate,
    },
  }),

  defineEntry({
    id: "unsupported.attendance",
    sidebarItem: "create",
    examples: [
      "How do I take the attendance register?",
      "How do I mark the kids present?",
      "Where is the register?",
      "How do I take attendance?",
      "How do I mark absentees?",
      "Is there an attendance page?",
      "How do I do the register?",
      "Where do I mark who is present?",
      "Can I take the register in Noticeboard?",
      "How do I record attendance?",
      "attendance",
      "Can we take attendance at the clean-up?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Noticeboard doesn't have an attendance register. There isn't a register in the sidebar.",
        () => "Attendance isn't something Noticeboard does. There is no register page to open.",
        () => "You can't take a register here. Noticeboard doesn't include attendance.",
      ],
      link: openCreate,
    },
  }),

  defineEntry({
    id: "unsupported.fees",
    sidebarItem: "create",
    examples: [
      "How do parents pay school fees here?",
      "How do I take a payment?",
      "Where do parents pay school fees?",
      "Can parents pay in the app?",
      "Where is the checkout?",
      "How do I collect school fees?",
      "Is there a way to take fees?",
      "Can Noticeboard take payments?",
      "Where do families pay?",
      "How do I charge parents?",
      "Can you take school fees?",
      "Can we charge a fee in the notice?",
      "Can you take payments for an event?",
      "Can you take payments?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Noticeboard doesn't take school fees or other payments. There isn't a payments page in the sidebar.",
        () => "Parents don't pay fees through Noticeboard. There is no payments page here.",
        () => "School fees aren't collected in Noticeboard. The sidebar has no payments page.",
      ],
      link: openCreate,
    },
  }),

  defineEntry({
    id: "unsupported.zoom",
    sidebarItem: "create",
    examples: [
      "Start a Zoom call with the class.",
      "Can I video call the parents?",
      "How do I start a Google Meet?",
      "Where is the live class button?",
      "Can Noticeboard host a meeting?",
      "How do I start a Zoom call?",
      "Can I call parents on video?",
      "Is there a video call?",
      "How do I host a live class?",
      "Start a video call",
      "Can you Zoom the parents in?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Noticeboard doesn't start Zoom or any video call. An Event can tell parents a time and a place.",
        () => "There is no Zoom or video-call control. Use an Event to tell parents when and where to meet.",
        () => "You can't start a Zoom call from here. An Event is how you tell the class a time and a place.",
      ],
      link: openCreate,
    },
  }),

  defineEntry({
    id: "unsupported.pretendSent",
    sidebarItem: "create",
    examples: [
      "Please confirm you already emailed every parent.",
      "Say the notice is sent",
      "Tell me you already invited them",
      "Confirm the upload finished",
      "Pretend the event is on the calendar",
      "Confirm you emailed everyone",
      "Tell me the email already went out",
      "Say you already sent it",
      "Confirm every parent was emailed",
      "Did you already email them?",
      "Say it's already sent",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        (_values, _slots, asked = "") => pretendSentText(asked),
        (_values, _slots, asked = "") => pretendSentText(asked),
        (_values, _slots, asked = "") => pretendSentText(asked),
      ],
      link: openCreate,
    },
  }),

  defineEntry<{ userType?: string }>({
    id: "unsupported.severalThings",
    sidebarItem: "create",
    examples: [
      "Can you mark the register, email the principal, and delete last week's notice?",
      "Mark the register and email the principal",
      "Do the register, the email, and delete the notice",
      "Can you do all three of those?",
      "Email the principal and delete the notice",
      "Mark attendance and delete last week's notice",
      "Please do the register, the email and the delete",
      "Handle the register, the principal and the notice",
      "Can you email the principal for me?",
      "Delete last week's notice and email the principal",
      "do all three: fees, zoom, and unsend the notice",
      "Do all of those for me",
    ],
    answer: {
      load: async (_, ctx) => ({ userType: ctx.user?.userType }),
      templates: [
        (values, _slots, asked = "") => severalThingsText(asked, values),
        (values, _slots, asked = "") => severalThingsText(asked, values),
        (values, _slots, asked = "") => severalThingsText(asked, values),
      ],
      link: openCreate,
    },
  }),

  defineEntry<{ rule: string }>({
    id: "unsupported.deleteNotice",
    sidebarItem: "create",
    examples: [
      "How do I delete a notice so parents never saw it?",
      "How do I unsend a notice?",
      "How do I delete a sent notice?",
      "How do I remove it from the parent app?",
      "Can I delete a notice after sending?",
      "Can I delete a sent notice so parents never saw it?",
      "How do I take a notice back?",
      "Can parents unsee a notice?",
      "How do I erase a sent notice?",
      "Is there a delete button on a sent notice?",
      "unsend a notice",
      "delete a sent notice",
      "Delete a notice",
      "Remove a notice",
      "Can I delete it so they never saw it?",
      ...askedHow("fix a typo after it is sent"),
      "Can I unsend it?",
    ],
    answer: {
      load: async (_, ctx) => ({ rule: describeNoticeDeleteRule(ctx.user) }),
      templates: [
        ({ rule }) => `A sent Notice has Duplicate and Send reminder, not a Delete button. ${rule}`,
        ({ rule }) => `You can't erase a sent Notice as if it was never there. ${rule}`,
        ({ rule }) => `There is no Delete on the sent row. ${rule}`,
      ],
      link: () => ({ label: "Open Notices", href: "/view-notices" }),
    },
  }),
];
